import type {
  ExportPresetId,
  ExportSetup,
  ExportTextStyle,
  PageNumberPosition,
  SceneBreakStyle,
} from '../types/exportSetup'
import { defaultPaperSize, findExportPreset } from './export/exportPresets'

// A page setup is how a document is laid out on paper: the page view draws it
// and exports use it by default. It is an export setup in custom mode, saved
// per document in the synced metadata. All measurements are in points and
// millimeters.

export type PageSetup = ExportSetup

const PAGE_SIZES: ExportSetup['pageSize'][] = ['a4', 'letter', 'legal', 'a5', 'six-nine']
const ALIGNMENTS: ExportTextStyle['alignment'][] = ['left', 'center', 'right', 'justify']
const TEXT_CASES: ExportTextStyle['textCase'][] = ['none', 'uppercase', 'small-caps']
const SCENE_BREAKS: SceneBreakStyle[] = ['rule', 'hash', 'asterisks', 'blank']
const PAGE_NUMBERS: PageNumberPosition[] = ['none', 'header', 'footer']
const COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i
const FALLBACK_FONT = 'Georgia'

/** The first family of a CSS font stack, without quotes. */
export function pageSetupFont(fontFamily: string | null | undefined): string {
  return (fontFamily || '').split(',')[0].replace(/['"]/g, '').trim().slice(0, 160) || FALLBACK_FONT
}

/** The page setup a document has before the writer changes it: Clean Document in its typeface. */
export function defaultPageSetup(fontFamily: string | null | undefined): PageSetup {
  return findExportPreset('document')!.build(pageSetupFont(fontFamily), defaultPaperSize())
}

export function clonePageSetup(setup: PageSetup): PageSetup {
  return JSON.parse(JSON.stringify(setup))
}

function num(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === 'number' ? value : Number.NaN
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback
}

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function oneOf<T>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? value as T : fallback
}

function color(value: unknown, fallback: string): string {
  return typeof value === 'string' && COLOR_RE.test(value.trim()) ? value.trim() : fallback
}

function textStyle(raw: any, fallback: ExportTextStyle): ExportTextStyle {
  return {
    fontFamily: typeof raw?.fontFamily === 'string' && raw.fontFamily.trim()
      ? pageSetupFont(raw.fontFamily)
      : fallback.fontFamily,
    fontSize: num(raw?.fontSize, 4, 144, fallback.fontSize),
    fontWeight: Math.round(num(raw?.fontWeight, 100, 900, fallback.fontWeight)),
    color: color(raw?.color, fallback.color),
    alignment: oneOf(raw?.alignment, ALIGNMENTS, fallback.alignment),
    lineHeight: num(raw?.lineHeight, 0.5, 4, fallback.lineHeight),
    spaceAfter: num(raw?.spaceAfter, 0, 200, fallback.spaceAfter),
    letterSpacing: num(raw?.letterSpacing, -5, 20, fallback.letterSpacing),
    textCase: oneOf(raw?.textCase, TEXT_CASES, fallback.textCase),
  }
}

/**
 * Validates a page setup read from sync, a manifest, or an import. Fields that
 * are missing or out of range take the default's value; anything that is not
 * an object is no page setup at all.
 */
export function normalizePageSetup(raw: any): PageSetup | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const d = defaultPageSetup(typeof raw.body?.fontFamily === 'string' ? raw.body.fontFamily : FALLBACK_FONT)
  const setup: PageSetup = {
    // The page view needs concrete styles, so a page setup never defers to the editor's.
    mode: 'custom',
    includeTitle: bool(raw.includeTitle, d.includeTitle),
    title: textStyle(raw.title, d.title),
    sectionTitle: textStyle(raw.sectionTitle, d.sectionTitle),
    body: textStyle(raw.body, d.body),
    headings: textStyle(raw.headings, d.headings),
    paragraphIndent: num(raw.paragraphIndent, 0, 100, d.paragraphIndent),
    pageSize: oneOf(raw.pageSize, PAGE_SIZES, d.pageSize),
    orientation: oneOf(raw.orientation, ['portrait', 'landscape'] as const, d.orientation),
    margins: num(raw.margins, 0, 100, d.margins),
    backgroundColor: color(raw.backgroundColor, d.backgroundColor),
    keepInlineEmphasis: bool(raw.keepInlineEmphasis, d.keepInlineEmphasis),
    keepInlineFonts: bool(raw.keepInlineFonts, d.keepInlineFonts),
    keepInlineSizes: bool(raw.keepInlineSizes, d.keepInlineSizes),
    keepInlineColors: bool(raw.keepInlineColors, d.keepInlineColors),
    keepHighlights: bool(raw.keepHighlights, d.keepHighlights),
    keepAlignment: bool(raw.keepAlignment, d.keepAlignment),
    widowOrphanControl: bool(raw.widowOrphanControl, d.widowOrphanControl),
    pageBreakBeforeTitle: bool(raw.pageBreakBeforeTitle, d.pageBreakBeforeTitle),
    indentFirstParagraph: bool(raw.indentFirstParagraph, d.indentFirstParagraph ?? true),
    sceneBreak: oneOf(raw.sceneBreak, SCENE_BREAKS, d.sceneBreak ?? 'asterisks'),
    pageNumbers: oneOf(raw.pageNumbers, PAGE_NUMBERS, d.pageNumbers ?? 'none'),
    pageNumberTitle: bool(raw.pageNumberTitle, d.pageNumberTitle ?? false),
    pageNumberOnFirstPage: bool(raw.pageNumberOnFirstPage, d.pageNumberOnFirstPage ?? true),
  }
  if (raw.marginLeft !== undefined) setup.marginLeft = num(raw.marginLeft, 0, 100, setup.margins)
  const preset: ExportPresetId | undefined = findExportPreset(raw.preset)?.id
  if (preset) setup.preset = preset
  return setup
}

/** Paper sizes in points, portrait. */
export const PAGE_SIZES_PT: Record<ExportSetup['pageSize'], [number, number]> = {
  a4: [595.28, 841.89],
  letter: [612, 792],
  legal: [612, 1008],
  a5: [419.53, 595.28],
  'six-nine': [432, 648],
}

export const MM_TO_PT = 72 / 25.4

/** The page's size and margins in points, with orientation applied. */
export function pageGeometry(setup: PageSetup) {
  const [w, h] = PAGE_SIZES_PT[setup.pageSize] ?? PAGE_SIZES_PT.a4
  const [width, height] = setup.orientation === 'landscape' ? [h, w] : [w, h]
  const margin = setup.margins * MM_TO_PT
  return {
    width,
    height,
    top: margin,
    right: margin,
    bottom: margin,
    left: (setup.marginLeft ?? setup.margins) * MM_TO_PT,
  }
}
